import { useEffect, useState } from 'react';
import {
  convertCandidateToEmployee,
  createJobOpening,
  generateOffer,
  listApplications,
  listDepartments,
  listEmployees,
  listJobOpenings,
  listOfferTemplates,
  parseResume,
  scheduleInterview,
  submitApplication,
  submitInterviewEvaluation,
  updateApplicationStage,
  type Department,
  type EmployeeRecord,
  type JobApplication,
  type JobOpening,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import { Icon } from '../components/Icon';
import { StatusBadge } from '../components/StatusBadge';

export function Recruitment() {
  const { session } = useAuth();
  const { formatMoney } = useLocalization();
  const [tab, setTab] = useState<'openings' | 'pipeline' | 'parser' | 'interviews' | 'offers'>('openings');
  const [jobs, setJobs] = useState<JobOpening[]>([]);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Filters
  const [selectedJobFilter, setSelectedJobFilter] = useState<string>('');

  // 1. Post Job Opening Modal
  const [jobModal, setJobModal] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [jobCode, setJobCode] = useState('');
  const [deptId, setDeptId] = useState('');
  const [vacancies, setVacancies] = useState(1);
  const [jobType, setJobType] = useState('full_time');
  const [expLevel, setExpLevel] = useState('Mid-Level (3-5 yrs)');
  const [jobLocation, setJobLocation] = useState('Bangalore / Hybrid');
  const [jobDesc, setJobDesc] = useState('');

  // 2. Candidate CV / Parser State
  const [rawCvText, setRawCvText] = useState('');
  const [parsedData, setParsedData] = useState<any | null>(null);
  const [parsing, setParsing] = useState(false);
  const [candJobId, setCandJobId] = useState('');
  const [candFirst, setCandFirst] = useState('');
  const [candLast, setCandLast] = useState('');
  const [candEmail, setCandEmail] = useState('');
  const [candPhone, setCandPhone] = useState('');
  const [candCompany, setCandCompany] = useState('');
  const [candExp, setCandExp] = useState(3);
  const [candCtc, setCandCtc] = useState(1500000);
  const [candNotice, setCandNotice] = useState(30);
  const [candSkills, setCandSkills] = useState('');

  // 3. Interview Schedule Modal
  const [interviewModal, setInterviewModal] = useState(false);
  const [selectedAppForInterview, setSelectedAppForInterview] = useState<JobApplication | null>(null);
  const [roundName, setRoundName] = useState('Technical Round 1');
  const [interviewerId, setInterviewerId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [meetingLink, setMeetingLink] = useState('https://meet.google.com/xyz-abcd-efg');

  // 4. Interview Evaluation Scorecard Modal
  const [evalModal, setEvalModal] = useState(false);
  const [selectedInterviewApp, setSelectedInterviewApp] = useState<JobApplication | null>(null);
  const [techScore, setTechScore] = useState(4);
  const [cultScore, setCultScore] = useState(5);
  const [commScore, setCommScore] = useState(4);
  const [recommendation, setRecommendation] = useState<'strong_hire' | 'hire' | 'hold' | 'reject'>('hire');
  const [strengths, setStrengths] = useState('Strong architectural foundation and hands-on system design.');
  const [evalFeedback, setEvalFeedback] = useState('Recommended for next leadership round.');

  // 5. Offer Generator & Conversion Modal
  const [offerModal, setOfferModal] = useState(false);
  const [selectedAppForOffer, setSelectedAppForOffer] = useState<JobApplication | null>(null);
  const [offeredDesignation, setOfferedDesignation] = useState('');
  const [offeredCtc, setOfferedCtc] = useState(1800000);
  const [offerJoiningDate, setOfferJoiningDate] = useState('2026-09-01');
  const [generatedOfferResult, setGeneratedOfferResult] = useState<any | null>(null);

  // 6. Convert to Employee Modal
  const [convertModal, setConvertModal] = useState(false);
  const [convertOfferId, setConvertOfferId] = useState('');
  const [convertDeptId, setConvertDeptId] = useState('');
  const [convertDesignation, setConvertDesignation] = useState('');
  const [convertCtc, setConvertCtc] = useState(1800000);
  const [convertManagerId, setConvertManagerId] = useState('');

  async function loadData() {
    try {
      const [jobsRes, appsRes, deptsRes, empsRes] = await Promise.all([
        listJobOpenings().catch(() => ({ data: [] })),
        listApplications().catch(() => ({ data: [] })),
        listDepartments().catch(() => ({ items: [] })),
        listEmployees().catch(() => ({ items: [] })),
        listOfferTemplates().catch(() => ({ data: [] })),
      ]);
      setJobs(jobsRes.data || []);
      setApplications(appsRes.data || []);
      setDepartments(deptsRes.items || []);
      setEmployees(empsRes.items || []);

      if (deptsRes.items?.length && !deptId) {
        setDeptId(deptsRes.items[0].id);
        setConvertDeptId(deptsRes.items[0].id);
      }
      if (jobsRes.data?.length && !candJobId) {
        setCandJobId(jobsRes.data[0].id);
      }
      if (empsRes.items?.length && !interviewerId) {
        setInterviewerId(empsRes.items[0].id);
      }
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load recruitment data' });
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  async function handleCreateJob(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createJobOpening({
        organizationId: session?.tenant.id || 'default-org',
        departmentId: deptId || null,
        title: jobTitle,
        code: jobCode,
        vacancies: Number(vacancies),
        jobType,
        experienceLevel: expLevel,
        location: jobLocation,
        description: jobDesc || null,
      });
      setMsg({ type: 'success', text: `Job opening "${jobTitle}" posted successfully!` });
      setJobModal(false);
      setJobTitle('');
      setJobCode('');
      setJobDesc('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to create job opening' });
    }
  }

  async function handleParseResume() {
    if (!rawCvText.trim()) return;
    try {
      setParsing(true);
      const res = await parseResume(rawCvText);
      setParsedData(res.data);
      if (res.data.firstName) setCandFirst(res.data.firstName);
      if (res.data.lastName) setCandLast(res.data.lastName);
      if (res.data.currentCompany) setCandCompany(res.data.currentCompany);
      if (res.data.email) setCandEmail(res.data.email);
      if (res.data.phone) setCandPhone(res.data.phone);
      if (res.data.skills?.length) setCandSkills(res.data.skills.join(', '));
      if (res.data.yearsOfExperience) setCandExp(res.data.yearsOfExperience);
      setMsg({ type: 'success', text: 'Resume parsed! Review the extracted candidate details below.' });
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to parse resume text' });
    } finally {
      setParsing(false);
    }
  }

  async function handleSubmitCandidate(e: React.FormEvent) {
    e.preventDefault();
    if (!candJobId) {
      setMsg({ type: 'error', text: 'Please select a job opening.' });
      return;
    }
    try {
      await submitApplication(candJobId, {
        firstName: candFirst,
        lastName: candLast,
        email: candEmail,
        phone: candPhone,
        currentCompany: candCompany,
        totalExperienceYears: candExp,
        expectedCtc: candCtc,
        noticePeriodDays: candNotice,
        skills: candSkills,
        rawResumeText: rawCvText || undefined,
      });
      setMsg({ type: 'success', text: `Candidate ${candFirst} ${candLast} added to recruitment pipeline!` });
      setCandFirst('');
      setCandLast('');
      setCandEmail('');
      setCandPhone('');
      setCandCompany('');
      setRawCvText('');
      setParsedData(null);
      await loadData();
      setTab('pipeline');
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit candidate profile' });
    }
  }

  async function handleUpdateStage(appId: string, stage: string) {
    try {
      await updateApplicationStage(appId, { stage });
      setMsg({ type: 'success', text: `Candidate stage updated to ${stage.toUpperCase()}` });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to update stage' });
    }
  }

  async function handleScheduleInterview(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedAppForInterview || !interviewerId) return;
    try {
      await scheduleInterview({
        applicationId: selectedAppForInterview.id,
        roundName,
        interviewerEmployeeId: interviewerId,
        scheduledAt: scheduledAt || new Date(Date.now() + 86400000).toISOString(),
        meetingLink,
      });
      setMsg({ type: 'success', text: `Interview scheduled with ${selectedAppForInterview.first_name} ${selectedAppForInterview.last_name}!` });
      setInterviewModal(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to schedule interview' });
    }
  }

  async function handleSubmitScorecard(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedInterviewApp) return;
    try {
      const computedOverall = Math.round((techScore + cultScore + commScore) / 3);
      await submitInterviewEvaluation({
        interviewId: selectedInterviewApp.id,
        technicalScore: techScore,
        culturalScore: cultScore,
        communicationScore: commScore,
        overallScore: computedOverall,
        recommendation,
        strengths,
        areasOfImprovement: evalFeedback,
        detailedFeedback: evalFeedback,
        isConfidential: true,
      });
      setMsg({ type: 'success', text: `Confidential evaluation scorecard submitted. Recommendation: ${recommendation.toUpperCase()}` });
      setEvalModal(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit evaluation' });
    }
  }

  async function handleGenerateOffer(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedAppForOffer) return;
    try {
      const res = await generateOffer({
        applicationId: selectedAppForOffer.id,
        offeredDesignation,
        offeredCtc: Number(offeredCtc),
        joiningDate: offerJoiningDate,
      });
      setGeneratedOfferResult(res.data);
      setConvertOfferId(res.data.offerId);
      setConvertDesignation(offeredDesignation);
      setConvertCtc(Number(offeredCtc));
      setMsg({ type: 'success', text: `Offer letter generated successfully for ${selectedAppForOffer.first_name}!` });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to generate offer letter' });
    }
  }

  async function handleConvertCandidate(e: React.FormEvent) {
    e.preventDefault();
    if (!convertOfferId) return;
    try {
      const res = await convertCandidateToEmployee({
        offerId: convertOfferId,
        departmentId: convertDeptId,
        designation: convertDesignation,
        ctcAnnual: Number(convertCtc),
        managerId: convertManagerId || null,
        dateOfJoining: offerJoiningDate,
        generateUserAccount: true,
      });
      setMsg({
        type: 'success',
        text: `Candidate successfully converted to Employee! Employee Code: ${res.data.employeeCode}. Onboarding workflow auto-provisioned.`,
      });
      setConvertModal(false);
      setOfferModal(false);
      setGeneratedOfferResult(null);
      await loadData();
      setTab('pipeline');
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to convert candidate to employee' });
    }
  }

  const filteredApps = selectedJobFilter
    ? applications.filter((a) => a.job_opening_id === selectedJobFilter)
    : applications;

  const stageCounts = {
    applied: applications.filter((a) => a.stage === 'applied').length,
    screening: applications.filter((a) => a.stage === 'screening').length,
    interview: applications.filter((a) => a.stage === 'interview').length,
    offer: applications.filter((a) => a.stage === 'offer').length,
    hired: applications.filter((a) => a.stage === 'hired').length,
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Enterprise Recruitment & Talent Acquisition</div>
          <h1>Candidate Portal & ATS Pipeline</h1>
        </div>
        <div className="toolbar">
          <button className="btn" onClick={() => setTab('parser')}>
            <Icon name="upload" /> Resume Parser & Apply
          </button>
          <button className="btn" onClick={() => setJobModal(true)}>
            <Icon name="plus" /> Post Job Opening
          </button>
        </div>
      </div>

      {msg && (
        <div className={`alert ${msg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <Icon name={msg.type === 'success' ? 'check' : 'alertCircle'} />
          <span>{msg.text}</span>
          <button className="btn-quiet" style={{ marginLeft: 'auto' }} onClick={() => setMsg(null)}>✕</button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-top">
            <div className="kpi-label">Active Job Openings</div>
            <div className="kpi-icon-badge" style={{ background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}>
              <Icon name="briefcase" size={18} />
            </div>
          </div>
          <div className="kpi-value">{jobs.length}</div>
          <div className="kpi-hint">{jobs.reduce((acc, j) => acc + (j.vacancies || 1), 0)} Total Vacancies</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-top">
            <div className="kpi-label">Candidates in Pipeline</div>
            <div className="kpi-icon-badge" style={{ background: 'rgba(56,189,248,0.12)', color: '#0284c7' }}>
              <Icon name="users" size={18} />
            </div>
          </div>
          <div className="kpi-value">{applications.length}</div>
          <div className="kpi-hint">{stageCounts.interview} in Active Interviews</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-top">
            <div className="kpi-label">Offers Extended</div>
            <div className="kpi-icon-badge" style={{ background: 'rgba(245,158,11,0.12)', color: '#d97706' }}>
              <Icon name="fileText" size={18} />
            </div>
          </div>
          <div className="kpi-value">{stageCounts.offer}</div>
          <div className="kpi-hint">Template-based generation</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-top">
            <div className="kpi-label">Hired & Onboarded</div>
            <div className="kpi-icon-badge" style={{ background: 'rgba(16,185,129,0.12)', color: '#059669' }}>
              <Icon name="userCheck" size={18} />
            </div>
          </div>
          <div className="kpi-value" style={{ color: '#10b981' }}>{stageCounts.hired}</div>
          <div className="kpi-hint">1-Click Auto-Converted</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tabs" style={{ marginTop: 24, marginBottom: 20 }}>
        <button className={`tab-btn ${tab === 'openings' ? 'active' : ''}`} onClick={() => setTab('openings')}>
          <Icon name="departments" /> Job Postings ({jobs.length})
        </button>
        <button className={`tab-btn ${tab === 'pipeline' ? 'active' : ''}`} onClick={() => setTab('pipeline')}>
          <Icon name="employees" /> ATS Pipeline ({applications.length})
        </button>
        <button className={`tab-btn ${tab === 'parser' ? 'active' : ''}`} onClick={() => setTab('parser')}>
          <Icon name="recruitment" /> Resume Parser & CV Portal
        </button>
        <button className={`tab-btn ${tab === 'interviews' ? 'active' : ''}`} onClick={() => setTab('interviews')}>
          <Icon name="calendar" /> Interview Scorecards
        </button>
        <button className={`tab-btn ${tab === 'offers' ? 'active' : ''}`} onClick={() => setTab('offers')}>
          <Icon name="award" /> Offer Letters & Conversion
        </button>
      </div>

      {/* TAB 1: JOB OPENINGS */}
      {tab === 'openings' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Current Job Requisitions</h2>
            <span className="muted" style={{ fontSize: 13 }}>{jobs.length} Positions Available</span>
          </div>
          {jobs.length === 0 ? (
            <div className="empty">No job openings created yet. Click "Post Job Opening" to begin hiring.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Job Code</th>
                  <th>Title</th>
                  <th>Department</th>
                  <th>Type & Experience</th>
                  <th>Location</th>
                  <th>Vacancies</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>{j.code}</td>
                    <td style={{ fontWeight: 600 }}>{j.title}</td>
                    <td>{j.department_name || 'General'}</td>
                    <td>
                      <span className="pill">{j.job_type.replace('_', ' ')}</span>
                      <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>{j.experience_level}</span>
                    </td>
                    <td>{j.location || 'HQ'}</td>
                    <td><strong>{j.vacancies}</strong></td>
                    <td><StatusBadge status={j.status} /></td>
                    <td>
                      <button
                        className="btn-quiet"
                        onClick={() => {
                          setSelectedJobFilter(j.id);
                          setTab('pipeline');
                        }}
                      >
                        View Candidates &rarr;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 2: ATS PIPELINE */}
      {tab === 'pipeline' && (
        <div className="panel">
          <div className="panel-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h2>Applicant Tracking Pipeline</h2>
              <select
                className="input"
                style={{ width: 220 }}
                value={selectedJobFilter}
                onChange={(e) => setSelectedJobFilter(e.target.value)}
              >
                <option value="">All Job Requisitions</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.title} ({j.code})</option>
                ))}
              </select>
            </div>
            <span className="muted" style={{ fontSize: 13 }}>{filteredApps.length} Candidates</span>
          </div>

          {filteredApps.length === 0 ? (
            <div className="empty">No applications found in this pipeline.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Job Requisition</th>
                  <th>Contact</th>
                  <th>Exp / Notice</th>
                  <th>Stage</th>
                  <th>Evaluation Rating</th>
                  <th>Progress Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{a.first_name} {a.last_name}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{a.current_company || 'Independent'}</div>
                    </td>
                    <td>
                      <div>{a.job_title}</div>
                      <div className="mono muted" style={{ fontSize: 11 }}>{a.job_code}</div>
                    </td>
                    <td>
                      <div>{a.email}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{a.phone || '—'}</div>
                    </td>
                    <td>
                      <div>Expected: {a.expected_ctc ? formatMoney(a.expected_ctc) : '—'}</div>
                      <div className="muted" style={{ fontSize: 12 }}>Notice: {a.notice_period_days || 30} days</div>
                    </td>
                    <td>
                      <StatusBadge status={a.stage} />
                    </td>
                    <td>
                      {a.rating ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#f59e0b', fontWeight: 600 }}>
                          ★ {a.rating}/5
                        </div>
                      ) : (
                        <span className="muted">Pending</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {a.stage === 'applied' && (
                          <button className="btn-quiet" onClick={() => handleUpdateStage(a.id, 'screening')}>
                            Screening
                          </button>
                        )}
                        {(a.stage === 'applied' || a.stage === 'screening') && (
                          <button
                            className="btn-quiet"
                            onClick={() => {
                              setSelectedAppForInterview(a);
                              setInterviewModal(true);
                            }}
                          >
                            Schedule Interview
                          </button>
                        )}
                        {a.stage === 'interview' && (
                          <>
                            <button
                              className="btn-quiet"
                              onClick={() => {
                                setSelectedInterviewApp(a);
                                setEvalModal(true);
                              }}
                            >
                              Scorecard
                            </button>
                            <button
                              className="btn-quiet"
                              onClick={() => {
                                setSelectedAppForOffer(a);
                                setOfferedDesignation(a.job_title);
                                setOfferedCtc(a.expected_ctc || 1500000);
                                setOfferModal(true);
                              }}
                            >
                              Generate Offer
                            </button>
                          </>
                        )}
                        {a.stage === 'offer' && (
                          <button
                            className="btn"
                            style={{ padding: '4px 10px', fontSize: 12, background: '#22c55e' }}
                            onClick={() => {
                              setSelectedAppForOffer(a);
                              setConvertDesignation(a.job_title);
                              setConvertCtc(a.expected_ctc || 1500000);
                              setConvertModal(true);
                            }}
                          >
                            1-Click Onboard
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 3: RESUME PARSER & CV PORTAL */}
      {tab === 'parser' && (
        <div className="resume-parser-grid">
          <div className="panel">
            <div className="panel-head">
              <h2>Upload or Paste CV / Resume Text</h2>
              <span className="muted" style={{ fontSize: 12 }}>Instant Regex & Keyword Extraction</span>
            </div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
              Paste candidate resume or CV text to automatically extract candidate name, email, contact number, estimated years of experience, and technical skill matches.
            </p>
            <textarea
              className="parser-textarea"
              rows={12}
              placeholder={`Example CV Text:
Rahul Sharma
Email: rahul.sharma@enterprise-tech.io | Phone: +91 91234 56789
Location: Bangalore, India
Lead Software Engineer with 7+ years of experience building scalable backend microservices.
Skills: TypeScript, Node.js, PostgreSQL, Kubernetes, AWS, CI/CD, React, Agile.`}
              value={rawCvText}
              onChange={(e) => setRawCvText(e.target.value)}
            />
            <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn-primary" onClick={handleParseResume} disabled={parsing || !rawCvText.trim()}>
                {parsing ? 'Parsing CV...' : 'Run Resume Parser'}
              </button>
              <button
                className="btn-quiet"
                onClick={() => {
                  setRawCvText(`Rahul Sharma
Email: rahul.sharma@enterprise-tech.io | Phone: +91 91234 56789
Location: Bangalore, India
Lead Software Engineer with 7+ years of experience building scalable backend microservices.
Skills: TypeScript, Node.js, PostgreSQL, Kubernetes, AWS, CI/CD, React, Agile.`);
                }}
              >
                Load Sample Resume
              </button>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head" style={{ alignItems: 'center' }}>
              <h2>Structured Candidate Profile</h2>
              {parsedData && (
                <span className="badge-pill-success">
                  <span className="badge-dot" />
                  Parsed Successfully
                </span>
              )}
            </div>
            <form onSubmit={handleSubmitCandidate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div className="form-field-block">
                  <label className="field-label">Target Job Opening</label>
                  <select
                    className="field-input"
                    value={candJobId}
                    onChange={(e) => setCandJobId(e.target.value)}
                    required
                  >
                    {jobs.map((j) => (
                      <option key={j.id} value={j.id}>{j.title} ({j.code})</option>
                    ))}
                  </select>
                </div>
                <div className="form-field-block">
                  <label className="field-label">Current Employer</label>
                  <input
                    className="field-input"
                    value={candCompany}
                    onChange={(e) => setCandCompany(e.target.value)}
                    placeholder="Current company name"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-field-block">
                  <label className="field-label">First Name</label>
                  <input
                    className="field-input"
                    value={candFirst}
                    onChange={(e) => setCandFirst(e.target.value)}
                    required
                    placeholder="e.g. Rahul"
                  />
                </div>
                <div className="form-field-block">
                  <label className="field-label">Last Name</label>
                  <input
                    className="field-input"
                    value={candLast}
                    onChange={(e) => setCandLast(e.target.value)}
                    required
                    placeholder="e.g. Sharma"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-field-block">
                  <label className="field-label">Email Address</label>
                  <input
                    className="field-input"
                    type="email"
                    value={candEmail}
                    onChange={(e) => setCandEmail(e.target.value)}
                    required
                    placeholder="name@enterprise-tech.io"
                  />
                </div>
                <div className="form-field-block">
                  <label className="field-label">Phone Number</label>
                  <input
                    className="field-input"
                    value={candPhone}
                    onChange={(e) => setCandPhone(e.target.value)}
                    placeholder="+91 91234 56789"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div className="form-field-block">
                  <label className="field-label">Experience (Yrs)</label>
                  <input
                    className="field-input"
                    type="number"
                    value={candExp}
                    onChange={(e) => setCandExp(Number(e.target.value))}
                  />
                </div>
                <div className="form-field-block">
                  <label className="field-label">Expected CTC (₹)</label>
                  <input
                    className="field-input"
                    type="number"
                    value={candCtc}
                    onChange={(e) => setCandCtc(Number(e.target.value))}
                  />
                </div>
                <div className="form-field-block">
                  <label className="field-label">Notice (Days)</label>
                  <input
                    className="field-input"
                    type="number"
                    value={candNotice}
                    onChange={(e) => setCandNotice(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="form-field-block">
                <label className="field-label">Extracted Skills (Comma Separated)</label>
                <input
                  className="field-input"
                  value={candSkills}
                  onChange={(e) => setCandSkills(e.target.value)}
                  placeholder="e.g. TypeScript, React, AWS, PostgreSQL"
                />
              </div>

              <button
                className="btn-primary"
                type="submit"
                style={{
                  width: '100%',
                  marginTop: 6,
                  padding: '12px 18px',
                  fontSize: 13.5,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <span>Submit Candidate Profile to Pipeline</span>
                <Icon name="arrowRight" size="xs" />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* TAB 4: INTERVIEWS & SCORECARDS */}
      {tab === 'interviews' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Interview Panel & Evaluation Scorecards</h2>
            <span className="muted" style={{ fontSize: 13 }}>Confidential ratings & panel feedback</span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Requisition</th>
                <th>Current Status</th>
                <th>Scorecard Evaluation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.filter((a) => a.stage === 'interview' || a.rating).map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600 }}>{a.first_name} {a.last_name}</td>
                  <td>{a.job_title}</td>
                  <td><StatusBadge status={a.stage} /></td>
                  <td>
                    {a.rating ? (
                      <div>
                        <span style={{ color: '#f59e0b', fontWeight: 600 }}>★ {a.rating}/5</span>
                        <div className="muted" style={{ fontSize: 12 }}>{a.feedback || 'Evaluated'}</div>
                      </div>
                    ) : (
                      <span className="muted">Scorecard Pending</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn-quiet"
                      onClick={() => {
                        setSelectedInterviewApp(a);
                        setEvalModal(true);
                      }}
                    >
                      Open Scorecard Form
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 5: OFFERS & CONVERSIONS */}
      {tab === 'offers' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Offer Letters & 1-Click Employee Onboarding</h2>
            <span className="muted" style={{ fontSize: 13 }}>Zero duplicate data entry conversion</span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Role</th>
                <th>Expected CTC</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.filter((a) => a.stage === 'interview' || a.stage === 'offer' || a.stage === 'hired').map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600 }}>{a.first_name} {a.last_name}</td>
                  <td>{a.job_title}</td>
                  <td>{a.expected_ctc ? formatMoney(a.expected_ctc) : '—'}</td>
                  <td><StatusBadge status={a.stage} /></td>
                  <td>
                    {a.stage !== 'hired' ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="btn-quiet"
                          onClick={() => {
                            setSelectedAppForOffer(a);
                            setOfferedDesignation(a.job_title);
                            setOfferedCtc(a.expected_ctc || 1500000);
                            setOfferModal(true);
                          }}
                        >
                          Generate Offer Letter
                        </button>
                        <button
                          className="btn"
                          style={{ padding: '4px 10px', fontSize: 12, background: '#22c55e' }}
                          onClick={() => {
                            setSelectedAppForOffer(a);
                            setConvertDesignation(a.job_title);
                            setConvertCtc(a.expected_ctc || 1500000);
                            setConvertModal(true);
                          }}
                        >
                          1-Click Onboard
                        </button>
                      </div>
                    ) : (
                      <span className="pill" style={{ background: '#22c55e', color: '#fff' }}>Hired & Onboarded</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL 1: POST JOB OPENING */}
      {jobModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-head">
              <h2>Create Job Requisition</h2>
              <button className="btn-quiet" onClick={() => setJobModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateJob}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Position Title</label>
                  <input
                    className="input"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    required
                    placeholder="e.g. Senior Frontend Engineer"
                  />
                </div>
                <div>
                  <label className="label">Job Code</label>
                  <input
                    className="input mono"
                    value={jobCode}
                    onChange={(e) => setJobCode(e.target.value)}
                    required
                    placeholder="REQ-2026-01"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Department</label>
                  <select
                    className="input"
                    value={deptId}
                    onChange={(e) => setDeptId(e.target.value)}
                  >
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Vacancies</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={vacancies}
                    onChange={(e) => setVacancies(Number(e.target.value))}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Job Type</label>
                  <select
                    className="input"
                    value={jobType}
                    onChange={(e) => setJobType(e.target.value)}
                  >
                    <option value="full_time">Full-Time Permanent</option>
                    <option value="contract">Fixed-Term Contract</option>
                    <option value="part_time">Part-Time</option>
                  </select>
                </div>
                <div>
                  <label className="label">Experience Level</label>
                  <input
                    className="input"
                    value={expLevel}
                    onChange={(e) => setExpLevel(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Work Location</label>
                <input
                  className="input"
                  value={jobLocation}
                  onChange={(e) => setJobLocation(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Job Description & Responsibilities</label>
                <textarea
                  className="input"
                  rows={3}
                  value={jobDesc}
                  onChange={(e) => setJobDesc(e.target.value)}
                  placeholder="Key responsibilities and qualifications required..."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setJobModal(false)}>Cancel</button>
                <button type="submit" className="btn">Post Opening</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: SCHEDULE INTERVIEW */}
      {interviewModal && selectedAppForInterview && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-head">
              <h2>Schedule Interview Round</h2>
              <button className="btn-quiet" onClick={() => setInterviewModal(false)}>✕</button>
            </div>
            <form onSubmit={handleScheduleInterview}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Candidate</label>
                <div style={{ fontWeight: 600 }}>{selectedAppForInterview.first_name} {selectedAppForInterview.last_name} ({selectedAppForInterview.job_title})</div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Round Name</label>
                <input
                  className="input"
                  value={roundName}
                  onChange={(e) => setRoundName(e.target.value)}
                  required
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Assign Panel Interviewer</label>
                <select
                  className="input"
                  value={interviewerId}
                  onChange={(e) => setInterviewerId(e.target.value)}
                  required
                >
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} — {emp.designation}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Date & Time</label>
                <input
                  className="input"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Meeting Video Link</label>
                <input
                  className="input"
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setInterviewModal(false)}>Cancel</button>
                <button type="submit" className="btn">Confirm Schedule</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: SCORECARD EVALUATION */}
      {evalModal && selectedInterviewApp && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 540 }}>
            <div className="modal-head">
              <h2>Interview Scorecard & Recommendation</h2>
              <button className="btn-quiet" onClick={() => setEvalModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmitScorecard}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600 }}>{selectedInterviewApp.first_name} {selectedInterviewApp.last_name}</div>
                <div className="muted" style={{ fontSize: 12 }}>Position: {selectedInterviewApp.job_title}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Technical (1-5)</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={5}
                    value={techScore}
                    onChange={(e) => setTechScore(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="label">Cultural (1-5)</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={5}
                    value={cultScore}
                    onChange={(e) => setCultScore(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="label">Communication</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={5}
                    value={commScore}
                    onChange={(e) => setCommScore(Number(e.target.value))}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Final Hiring Recommendation</label>
                <select
                  className="input"
                  value={recommendation}
                  onChange={(e: any) => setRecommendation(e.target.value)}
                >
                  <option value="strong_hire">Strong Hire (Top 5%)</option>
                  <option value="hire">Hire (Meets Requisite Bar)</option>
                  <option value="hold">Hold / Re-evaluate</option>
                  <option value="reject">Reject</option>
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label className="label">Key Strengths</label>
                <textarea
                  className="input"
                  rows={2}
                  value={strengths}
                  onChange={(e) => setStrengths(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Areas of Improvement / Detailed Notes</label>
                <textarea
                  className="input"
                  rows={2}
                  value={evalFeedback}
                  onChange={(e) => setEvalFeedback(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setEvalModal(false)}>Cancel</button>
                <button type="submit" className="btn">Submit Scorecard</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: GENERATE OFFER LETTER */}
      {offerModal && selectedAppForOffer && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 580 }}>
            <div className="modal-head">
              <h2>Generate Offer Letter</h2>
              <button className="btn-quiet" onClick={() => setOfferModal(false)}>✕</button>
            </div>
            {!generatedOfferResult ? (
              <form onSubmit={handleGenerateOffer}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label className="label">Candidate Name</label>
                    <input
                      className="input"
                      value={`${selectedAppForOffer.first_name} ${selectedAppForOffer.last_name}`}
                      disabled
                    />
                  </div>
                  <div>
                    <label className="label">Offered Designation</label>
                    <input
                      className="input"
                      value={offeredDesignation}
                      onChange={(e) => setOfferedDesignation(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label className="label">Annual CTC (₹)</label>
                    <input
                      className="input"
                      type="number"
                      value={offeredCtc}
                      onChange={(e) => setOfferedCtc(Number(e.target.value))}
                      required
                    />
                  </div>
                  <div>
                    <label className="label">Target Joining Date</label>
                    <input
                      className="input"
                      type="date"
                      value={offerJoiningDate}
                      onChange={(e) => setOfferJoiningDate(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button type="button" className="btn-quiet" onClick={() => setOfferModal(false)}>Cancel</button>
                  <button type="submit" className="btn">Generate & Preview Letter</button>
                </div>
              </form>
            ) : (
              <div>
                <div className="alert alert-success" style={{ marginBottom: 16 }}>
                  <Icon name="check" />
                  <span>Offer letter rendered with dynamic merge tags. Ready for dispatch or 1-click onboard.</span>
                </div>
                <div
                  style={{
                    background: '#0b0f19',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    padding: 16,
                    fontFamily: 'monospace',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    maxHeight: 240,
                    overflowY: 'auto',
                    marginBottom: 16,
                  }}
                >
                  {generatedOfferResult.renderedLetter}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button className="btn-quiet" onClick={() => setOfferModal(false)}>Close</button>
                  <button
                    className="btn"
                    style={{ background: '#22c55e' }}
                    onClick={() => {
                      setConvertModal(true);
                    }}
                  >
                    Proceed to 1-Click Onboarding &rarr;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL 5: 1-CLICK CANDIDATE TO EMPLOYEE CONVERSION */}
      {convertModal && selectedAppForOffer && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 540 }}>
            <div className="modal-head">
              <h2>1-Click Candidate &rarr; Employee Onboarding</h2>
              <button className="btn-quiet" onClick={() => setConvertModal(false)}>✕</button>
            </div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
              This automatically creates an employee record, salary structure, login credentials, and initializes the 6-stage onboarding checklist with zero duplicate data entry.
            </p>
            <form onSubmit={handleConvertCandidate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Full Name</label>
                  <input
                    className="input"
                    value={`${selectedAppForOffer.first_name} ${selectedAppForOffer.last_name}`}
                    disabled
                  />
                </div>
                <div>
                  <label className="label">Department</label>
                  <select
                    className="input"
                    value={convertDeptId}
                    onChange={(e) => setConvertDeptId(e.target.value)}
                    required
                  >
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Designation</label>
                  <input
                    className="input"
                    value={convertDesignation}
                    onChange={(e) => setConvertDesignation(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label">Annual CTC (₹)</label>
                  <input
                    className="input"
                    type="number"
                    value={convertCtc}
                    onChange={(e) => setConvertCtc(Number(e.target.value))}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Reporting Manager</label>
                <select
                  className="input"
                  value={convertManagerId}
                  onChange={(e) => setConvertManagerId(e.target.value)}
                >
                  <option value="">No Manager / Direct Executive</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} — {emp.designation}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setConvertModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ background: '#22c55e' }}>
                  Execute Employee Conversion &rarr;
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
